/**
 * Ende-zu-Ende im Abgleich (E21, 25.10.2026): verschlüsseln beim Senden,
 * öffnen beim Holen.
 *
 * Lokal bleibt alles Klartext, die Outbox auch — verschlüsselt wird erst, wenn
 * eine Änderung das Gerät verlässt. So ändert sich an keiner Schreibfunktion
 * etwas, und eine Tabelle kommt dazu, indem sie in `SEALED_EMPTY` steht.
 *
 * Ohne Schlüssel (Konto verschlüsselt, Gerät nicht entsperrt):
 * - Senden **hält** Änderungen verschlüsselter Tabellen in der Outbox — nie
 *   als Klartext hinaus, nie verworfen.
 * - Holen legt Verschlossenes in `sealed_rows`, bis das Gerät entsperrt ist.
 */

import {
  EMPTY_CONTENT_REASON,
  ENCRYPTED_TABLES,
  incomingWins,
  isEncryptedTable,
  isAlwaysSealed,
  isForbiddenEmpty,
  isSealedEmpty,
} from '@kraftwerk/shared';
import type { EncryptedTable, SyncTable, SyncedRow } from '@kraftwerk/shared';
import type { KraftwerkDatabase, OutboxEntry } from '../db/local.js';
import { currentOwnerId } from '../auth.js';
import { mayWriteEater } from '../db/householdMeals.js';
import { stamp } from '../db/write.js';
import { newId } from '@kraftwerk/shared';
import {
  isSealed,
  keyForRow,
  knownKeys,
  openRow,
  readAccountKey,
  sealNotice,
  sealRow,
} from '../crypto/e2e.js';

export interface Prepared {
  entry: OutboxEntry;
  row: Record<string, unknown>;
}

/**
 * Was jetzt hinaus darf, schon verschlüsselt. Gehaltene Einträge fehlen und
 * bleiben in der Outbox.
 */
export async function prepareForPush(
  entries: readonly OutboxEntry[],
  database: KraftwerkDatabase,
): Promise<Prepared[]> {
  const out: Prepared[] = [];
  const envelopes: OutboxEntry[] = [];
  const empty: OutboxEntry[] = [];
  for (const entry of entries) {
    const row = entry.row as Record<string, unknown>;
    if (!isEncryptedTable(entry.table)) {
      out.push({ entry, row });
      continue;
    }
    // Eine Hülle ist kein Klartext (26.10.2026): neu verschlüsselt würde aus
    // ihr ein leerer Eintrag, der beim Server den echten überschreibt.
    if (isSealed(row)) {
      envelopes.push(entry);
      continue;
    }
    // Leerer Inhalt, wo es keinen gibt (27.10.2026): Der Server kann das
    // Chiffrat nicht prüfen und nähme es an. Zurückhalten, nie verwerfen.
    if (entry.kind !== 'delete' && isForbiddenEmpty(entry.table, row)) {
      empty.push(entry);
      continue;
    }
    // Geteilt: Haushaltsschlüssel, privat: Kontoschlüssel (E21).
    const { key, hold } = await keyForRow(row, database);
    if (key) out.push({ entry, row: await withNotice(await sealRow(entry.table, row, key), key) });
    // Verschlüsselt, aber dieses Gerät hat den Schlüssel nicht: halten.
    // Standort (27.10.2026) nie als Klartext — auch nicht, solange der
    // Haushalt noch keinen Schlüssel hat (`ALWAYS_SEALED_TABLES`).
    else if (!hold && !isAlwaysSealed(entry.table)) out.push({ entry, row });
  }
  if (envelopes.length > 0) await dropEnvelopeEntries(database, envelopes);
  if (empty.length > 0) {
    const live = entries.filter((entry) => !envelopes.includes(entry));
    await holdEmptyEntries(database, empty, live);
  }
  return withoutHeldReferences(entries, out);
}

/**
 * Was auf eine gehaltene Zeile verweist, wartet mit ihr (27.10.2026): Eine
 * Aufgabe in einer Liste, deren Name noch auf den Schlüssel wartet, ging
 * vorher allein hinaus, der Server kannte die Liste nicht und wies sie ab —
 * nach fünf Versuchen hing sie als „abgelehnt“ fest, auch nachdem die Liste
 * längst angekommen war. Verweise sind Kennungen (UUID); jedes Feld, das die
 * Kennung einer gehaltenen Zeile trägt, hält die Zeile mit, auch über
 * mehrere Ebenen (Unteraufgabe → Aufgabe → Liste).
 */
function withoutHeldReferences(entries: readonly OutboxEntry[], out: Prepared[]): Prepared[] {
  const sending = new Set(out.map(({ entry }) => entry));
  const held = new Set<string>();
  for (const entry of entries) {
    const id = (entry.row as { id?: unknown }).id;
    if (!sending.has(entry) && entry.kind !== 'delete' && typeof id === 'string') held.add(id);
  }
  if (held.size === 0) return out;
  let rest = out;
  for (;;) {
    const next = rest.filter(({ entry }) => {
      const row = entry.row as Record<string, unknown>;
      return !Object.entries(row).some(
        ([field, value]) => field !== 'id' && typeof value === 'string' && held.has(value),
      );
    });
    if (next.length === rest.length) return next;
    for (const { entry } of rest) {
      if (next.some((kept) => kept.entry === entry)) continue;
      const id = (entry.row as { id?: unknown }).id;
      if (typeof id === 'string') held.add(id);
    }
    rest = next;
  }
}

/**
 * Mitteilung eines Check-ins (27.10.2026): lokal Klartext, hinaus nur
 * verschlüsselt mit demselben Haushaltsschlüssel. Schon Verschlüsseltes
 * (vom Server zurückgeholt) bleibt, wie es ist; der Server nimmt sie ohnehin
 * nur beim Anlegen an.
 */
async function withNotice(
  row: Record<string, unknown>,
  key: CryptoKey,
): Promise<Record<string, unknown>> {
  const notice = row.notice;
  if (notice === undefined || notice === null || typeof notice === 'string') return row;
  return { ...row, notice: await sealNotice(notice, key) };
}

/**
 * Leere Einträge mit Grund in der Outbox stehen lassen — sichtbar, kein
 * Fehlversuch. Kam danach eine Änderung derselben Zeile, die gesendet werden
 * darf, trägt sie die ganze Zeile: der leere Stand ist überholt und geht.
 */
async function holdEmptyEntries(
  database: KraftwerkDatabase,
  held: readonly OutboxEntry[],
  all: readonly OutboxEntry[],
): Promise<void> {
  const superseded = (entry: OutboxEntry) =>
    all.some(
      (later) =>
        later.table === entry.table &&
        (later.row as SyncedRow).id === (entry.row as SyncedRow).id &&
        (later.seq ?? 0) > (entry.seq ?? 0) &&
        !held.includes(later),
    );
  await database.transaction('rw', database.outbox, async () => {
    for (const entry of held) {
      if (entry.seq === undefined) continue;
      if (superseded(entry)) await database.outbox.delete(entry.seq);
      else if (entry.lastError !== EMPTY_CONTENT_REASON) {
        await database.outbox.update(entry.seq, { lastError: EMPTY_CONTENT_REASON });
      }
    }
  });
}

/** Wie viele Änderungen leer zurückgehalten werden — für die Anzeige. */
export async function heldEmptyCount(database: KraftwerkDatabase): Promise<number> {
  return database.outbox.filter((entry) => entry.lastError === EMPTY_CONTENT_REASON).count();
}

/**
 * Ausgehende Hüllen verwerfen und die Zeile zum Öffnen zurücklegen. Verworfen
 * wird nur eine Kopie dessen, was beim Server liegt — nie eine Eingabe.
 */
async function dropEnvelopeEntries(
  database: KraftwerkDatabase,
  entries: readonly OutboxEntry[],
): Promise<void> {
  await database.transaction('rw', database.outbox, async () => {
    for (const entry of entries) {
      if (entry.seq !== undefined) await database.outbox.delete(entry.seq);
    }
  });
  await quarantineEnvelopes(database, [...new Set(entries.map((entry) => entry.table))]);
}

/**
 * Hüllen, die lokal wie Klartext liegen, in `sealed_rows` legen (26.10.2026).
 *
 * Eine ältere App-Fassung kannte `payload` nicht und legte verschlüsselt
 * Geholtes so ab, wie es kam: mit leeren Feldern. Am 26.10. verschickte ein
 * solches Gerät beim Einrichten alles neu — verschlüsselt, aber leer, und
 * die Einkaufsliste war beim Server weg. Lokal ist Klartext ohne `payload`
 * (`openRow` nimmt es ab); was eins trägt, wartet aufs Öffnen.
 */
export async function quarantineEnvelopes(
  database: KraftwerkDatabase,
  tables: readonly string[] = ENCRYPTED_TABLES,
): Promise<number> {
  let moved = 0;
  for (const table of tables) {
    if (!isEncryptedTable(table)) continue;
    const target = database.tableOf(table);
    const envelopes = ((await target.toArray()) as SyncedRow[]).filter((row) =>
      isSealed(row as unknown as Record<string, unknown>),
    );
    if (envelopes.length === 0) continue;
    const ids = new Set(envelopes.map((row) => row.id));
    await database.transaction('rw', [target, database.sealed_rows, database.outbox], async () => {
      await database.sealed_rows.bulkPut(
        envelopes.map((row) => ({ key: `${table}:${row.id}`, table, row })),
      );
      await target.bulkDelete([...ids]);
      await database.outbox
        .where('table')
        .equals(table)
        .filter((entry) => ids.has((entry.row as SyncedRow).id))
        .delete();
    });
    moved += envelopes.length;
  }
  return moved;
}

const ENVELOPES_CHECKED_META = 'e2eEnvelopesChecked';

/** Einmal je Gerät: liegengebliebene Hüllen zurücklegen und öffnen. */
export async function repairEnvelopesOnce(database: KraftwerkDatabase): Promise<number> {
  if ((await database.meta.get(ENVELOPES_CHECKED_META))?.value === '1') return 0;
  const moved = await quarantineEnvelopes(database);
  if (moved > 0) await openHeldRows(database);
  await database.meta.put({ key: ENVELOPES_CHECKED_META, value: '1' });
  return moved;
}

/**
 * Öffnen mit dem passenden Schlüssel — und sonst mit jedem, den das Gerät
 * kennt. Zieht ein Haushalt um (`joinHousehold`), verschiebt der Server die
 * geteilten Zeilen, ihr Chiffrat gehört aber noch zum alten Schlüssel.
 * `fitting: false` heißt: neu verschlüsseln, damit die anderen es lesen.
 */
async function openWithAnyKey(
  table: EncryptedTable,
  row: Record<string, unknown>,
  database: KraftwerkDatabase,
): Promise<{ plain: Record<string, unknown>; fitting: boolean } | null> {
  const { key } = await keyForRow(row, database);
  if (key) {
    try {
      return { plain: await openRow(table, row, key), fitting: true };
    } catch {
      // Falscher Schlüssel — weiter mit den anderen.
    }
  }
  for (const other of await knownKeys(database)) {
    if (other === key) continue;
    try {
      return { plain: await openRow(table, row, other), fitting: false };
    } catch {
      // Nächster.
    }
  }
  return null;
}

export interface Accepted {
  /** Bereit für die Tabelle. */
  rows: SyncedRow[];
  /**
   * Nach dem Speichern neu senden: mit einem fremden Schlüssel geöffnet —
   * oder beim Server im Klartext, obwohl das Konto verschlüsselt.
   */
  reseal: SyncedRow[];
}

/**
 * Liegt beim Server Klartext, den dieses Gerät verschlüsseln kann
 * (27.10.2026)? „Vorlagen nachladen“ legt Übungen und Plan-Übungen auf dem
 * Server an — öffentliche Vorlagen, aber im verschlüsselten Konto bliebe ihr
 * Text lesbar, bis jemand sie ändert. Nur Privates mit Inhalt: Geteiltes
 * verschlüsselt das Gerät, das den Haushaltsschlüssel anlegt (ein kleines Kind
 * dürfte es gar nicht senden), und ein leerer Umschlag — etwa ein Tagebuchtag,
 * den ein Kurzbefehl nur mit Gewicht anlegte — hat nichts zu verbergen.
 */
async function plaintextAtServer(
  table: EncryptedTable,
  row: Record<string, unknown>,
  database: KraftwerkDatabase,
): Promise<boolean> {
  if (row.deletedAt || typeof row.householdId === 'string') return false;
  if (isSealedEmpty(table, row)) return false;
  return (await readAccountKey(database)) !== null;
}

/**
 * Geholte Zeilen einer Tabelle vorbereiten — **vor** der Dexie-Transaktion:
 * Entschlüsseln ist ein fremdes Versprechen, auf das eine Dexie-Transaktion
 * nicht warten kann. Verschlossenes ohne Schlüssel wandert in `sealed_rows`.
 */
export async function acceptPulled(
  table: SyncTable,
  rows: readonly SyncedRow[],
  database: KraftwerkDatabase,
): Promise<Accepted> {
  if (!isEncryptedTable(table)) return { rows: [...rows], reseal: [] };
  const ready: SyncedRow[] = [];
  const reseal: SyncedRow[] = [];
  const held: SyncedRow[] = [];
  for (const row of rows) {
    const record = row as unknown as Record<string, unknown>;
    if (!isSealed(record)) {
      ready.push(row);
      if (await plaintextAtServer(table, record, database)) reseal.push(row);
      continue;
    }
    const opened = await openWithAnyKey(table, record, database);
    if (!opened) {
      held.push(row);
      continue;
    }
    const plain = opened.plain as unknown as SyncedRow;
    ready.push(plain);
    if (!opened.fitting) reseal.push(plain);
  }
  if (held.length > 0) {
    await database.sealed_rows.bulkPut(
      held.map((row) => ({ key: `${table}:${row.id}`, table, row })),
    );
  }
  return { rows: ready, reseal };
}

/** Nach dem Entsperren oder neuem Haushaltsschlüssel: Wartendes öffnen. */
export async function openHeldRows(database: KraftwerkDatabase): Promise<number> {
  const held = await database.sealed_rows.toArray();
  let opened = 0;
  for (const { key: holdKey, table, row } of held) {
    if (!isEncryptedTable(table)) continue;
    const result = await openWithAnyKey(table, row as never, database);
    if (!result) continue;
    const plain = result.plain as unknown as SyncedRow;
    const target = database.tableOf(table);
    await database.transaction('rw', [target, database.sealed_rows], async () => {
      const existing = (await target.get(plain.id)) as SyncedRow | undefined;
      if (incomingWins(plain, existing)) await target.put(plain as never);
      await database.sealed_rows.delete(holdKey);
    });
    if (!result.fitting) await requeueRows(database, table, [plain]);
    opened += 1;
  }
  return opened;
}

/** Wie viele geholte Zeilen auf das Entsperren warten. */
export function heldCount(database: KraftwerkDatabase): Promise<number> {
  return database.sealed_rows.count();
}

/**
 * Nach dem Einrichten: Was der Server bisher im Klartext hat, neu senden —
 * jetzt verschlüsselt. Auch Gelöschtes, sonst bliebe dort sein alter Text.
 */
export async function resealAll(
  database: KraftwerkDatabase,
  householdId: string | null = null,
  tables: readonly EncryptedTable[] = ENCRYPTED_TABLES,
  privateOnly = false,
): Promise<number> {
  let count = 0;
  for (const table of tables) {
    const target = database.tableOf(table);
    // Mit Haushalt: nur dessen geteilte Zeilen (neuer Haushaltsschlüssel).
    // `privateOnly` (Kind): nur Eigenes ohne Haushalt — Geteiltes anderer
    // neu zu senden, wiese der Server einem kleinen Kind ab.
    const all = (await target.toArray()) as SyncedRow[];
    const shared = (row: SyncedRow) => (row as unknown as { householdId?: string }).householdId;
    const rows = householdId
      ? all.filter((row) => shared(row) === householdId)
      : privateOnly
        ? all.filter((row) => !shared(row))
        : all;
    count += await requeueRows(database, table, await resendable(database, table, rows));
  }
  return count;
}

/**
 * Nur, was dieses Konto selbst schreiben darf (27.10.2026): Die Mitess-Zeile
 * eines anderen Erwachsenen wiese der Server ab (`eaterWriteRule`) — sie
 * stünde danach als „abgelehnt“ in der Outbox. Die sendet sein Gerät neu.
 */
async function resendable(
  database: KraftwerkDatabase,
  table: EncryptedTable,
  rows: readonly SyncedRow[],
): Promise<readonly SyncedRow[]> {
  if (table !== 'household_meal_eaters' || rows.length === 0) return rows;
  const me = currentOwnerId();
  const members = await database.household_members.toArray();
  const live = members.filter((member) => !member.deletedAt);
  return rows.filter((row) => {
    const eater = row as unknown as { householdId: string; eaterId: string };
    const here = live.filter((member) => member.householdId === eater.householdId);
    return mayWriteEater(
      here.find((member) => member.userId === me),
      here.find((member) => member.userId === eater.eaterId),
    );
  });
}

/**
 * Zeilen mit frischem Zeitstempel neu senden — lokal und Outbox in einer
 * Transaktion. Auch Gelöschtes, sonst bliebe beim Server sein alter Inhalt.
 */
export async function requeueRows(
  database: KraftwerkDatabase,
  table: EncryptedTable,
  rows: readonly SyncedRow[],
): Promise<number> {
  if (rows.length === 0) return 0;
  const now = stamp();
  const target = database.tableOf(table);
  let count = 0;
  let envelopes = false;
  await database.transaction('rw', [target, database.outbox, database.sealed_rows], async () => {
    for (const row of rows) {
      const current = ((await target.get(row.id)) as SyncedRow | undefined) ?? row;
      // Nie eine Hülle neu senden — siehe `quarantineEnvelopes`.
      if (isSealed(current as unknown as Record<string, unknown>)) {
        envelopes = true;
        continue;
      }
      // Beim Server liegt eine neuere Fassung, die dieses Gerät noch nicht
      // öffnen kann (27.10.2026): Mit frischem Zeitstempel überschriebe die
      // ältere lokale sie dort — und gewönne danach auch beim Öffnen hier.
      // Sie ist dort schon Chiffrat; neu zu senden gibt es nichts.
      const held = await database.sealed_rows.get(`${table}:${current.id}`);
      if (held && !incomingWins(current, held.row)) continue;
      count += 1;
      const next = { ...current, updatedAt: now };
      await target.put(next as never);
      await database.outbox.add({
        mutationId: newId(),
        table,
        kind: next.deletedAt ? 'delete' : 'upsert',
        row: next,
        createdAt: now,
        attempts: 0,
      });
    }
  });
  if (envelopes) await quarantineEnvelopes(database, [table]);
  return count;
}

const SEALED_TABLES_META = 'e2eSealedTables';

/** Merken: der Bestand dieser Tabellen ging verschlüsselt neu. */
export async function markTablesResealed(
  tables: readonly string[],
  database: KraftwerkDatabase,
): Promise<void> {
  const done = new Set(await readResealedTables(database));
  for (const table of tables) done.add(table);
  await database.meta.put({ key: SEALED_TABLES_META, value: JSON.stringify([...done].sort()) });
}

async function readResealedTables(database: KraftwerkDatabase): Promise<string[]> {
  const value = (await database.meta.get(SEALED_TABLES_META))?.value;
  try {
    return value ? (JSON.parse(value) as string[]) : [];
  } catch {
    return [];
  }
}

/**
 * Neu verschlüsselte Tabellen nachholen (25.10.2026): Den Bestand sendet das
 * Einrichten neu — für Tabellen, die erst später dazukommen, gab es diesen
 * Schritt nicht, und ihr Klartext bliebe beim Server, bis jemand jede Zeile
 * ändert. Einmal je Tabelle und Gerät, nur mit Kontoschlüssel.
 */
export async function resealNewTables(
  database: KraftwerkDatabase,
  privateOnly = false,
): Promise<number> {
  if (!(await readAccountKey(database))) return 0;
  const done = new Set(await readResealedTables(database));
  // Geräte von vor dieser Liste haben die Gedanken beim Einrichten neu gesendet.
  if (done.size === 0) done.add('journal_notes');
  const missing = ENCRYPTED_TABLES.filter((table) => !done.has(table));
  if (missing.length === 0) return 0;
  const count = await resealAll(database, null, missing, privateOnly);
  await markTablesResealed([...done, ...missing], database);
  return count;
}
